/**
 * Authentication store — Zustand-backed wrapper around Supabase Auth.
 *
 * Responsibilities:
 *   1. Restore the session from local storage on app boot (`initialize()`).
 *   2. Subscribe to Supabase's onAuthStateChange so sign-in / sign-out from
 *      any tab keeps every component in sync.
 *   3. Resolve the session to a profile: staff sessions get their
 *      `public.users` row (role + venue scope, used by route guards),
 *      consumer sessions get their `public.profiles` row (schema §17) —
 *      created on first session if missing.
 *   4. Expose auth actions (`signIn`, `signUp`, Google OAuth, password reset,
 *      account deletion, …) that components can call without knowing anything
 *      about Supabase.
 *
 * The store stays tiny on purpose — anything role-specific (route gating,
 * page chrome, the consumer sidebar) lives in components that subscribe to
 * the slices they need.
 */

import { create } from "zustand";
import type { Session, User } from "@supabase/supabase-js";
import { supabase } from "@/lib/supabase";
import { getAnonId } from "@/lib/anonId";
import { clearDeviceAfterAccountDeletion } from "@/lib/deviceData";
import { useThemeStore, type Theme } from "@/services/themeStore";
import type { ConsumerProfile, UserProfile } from "@/types/auth";

export type AuthStatus = "initializing" | "signed_out" | "signed_in";

export interface SignInResult {
  ok: boolean;
  error?: string;
  /** signUp only: the account needs email confirmation before it has a
   *  session (the dashboard "Confirm email" toggle is on — env.AUTH_EMAIL_CONFIRM). */
  needsEmailConfirm?: boolean;
}

export interface AuthState {
  status: AuthStatus;
  session: Session | null;
  user: User | null;
  /** Staff profile (`public.users`) — operators / super-admins / managers. */
  profile: UserProfile | null;
  /** Consumer profile (`public.profiles`, §17). Mutually exclusive with `profile`. */
  consumerProfile: ConsumerProfile | null;
  /** True while the profile row is being fetched for the current session.
   *  Lets route guards show a spinner instead of flashing the "not
   *  provisioned" screen before the profile lands. */
  profileLoading: boolean;
  /** Last sign-in error (e.g. "Invalid login credentials"). */
  lastError: string | null;
  /** The session came from a password-recovery email — the consumer sidebar
   *  should open straight into its set-new-password view. */
  recoveryPending: boolean;

  initialize: () => Promise<void>;
  // `captchaToken` (Turnstile, single-use) is required by Supabase on the
  // password/signup/recover/resend endpoints once dashboard captcha is on;
  // Google (PKCE), refresh and updateUser are exempt. Optional so a build
  // without VITE_TURNSTILE_SITE_KEY keeps today's call shape.
  signIn: (email: string, password: string, captchaToken?: string) => Promise<SignInResult>;
  signUp: (
    email: string,
    password: string,
    displayName: string,
    captchaToken?: string,
  ) => Promise<SignInResult>;
  signInWithGoogle: () => Promise<SignInResult>;
  /** Same PKCE redirect flow as Google; offered only when `env.AUTH_APPLE`. */
  signInWithApple: () => Promise<SignInResult>;
  /** Always "succeeds" from the caller's point of view — the UI shows neutral
   *  copy either way so the form can't be used to probe which emails exist. */
  resetPassword: (email: string, captchaToken?: string) => Promise<SignInResult>;
  resendConfirmEmail: (email: string, captchaToken?: string) => Promise<SignInResult>;
  updatePassword: (password: string) => Promise<SignInResult>;
  updateDisplayName: (name: string) => Promise<SignInResult>;
  /** Apply + persist the theme: always on-device (themeStore/localStorage);
   *  written through to profiles.theme (§26) for signed-in consumers. */
  updateTheme: (theme: Theme) => Promise<SignInResult>;
  /** GDPR erasure — `delete_own_account()` RPC (§17c), staff-guarded server-side. */
  deleteAccount: () => Promise<SignInResult>;
  clearRecovery: () => void;
  signOut: () => Promise<void>;
  /** Re-fetch the profile row, e.g. after a role change. */
  refreshProfile: () => Promise<void>;
}

/**
 * Shared "is this a signed-in user?" selector. While auth is still
 * `initializing` this reads FALSE — account-gated surfaces (live POS capacity,
 * the Offers tab) treat an unknown session as a guest, so a guest can never
 * flash gated content; a signed-in user at worst sees the guest view for the
 * few ms the localStorage session read takes.
 */
export const selectSignedIn = (s: AuthState) => s.status === "signed_in" && s.user !== null;

async function loadStaffProfile(userId: string): Promise<UserProfile | null> {
  const { data, error } = await supabase
    .from("users")
    .select("id, role, restaurant_id, email, display_name")
    .eq("id", userId)
    .maybeSingle();
  if (error) {
    console.warn("[authStore] staff profile fetch failed:", error.message);
    return null;
  }
  return (data as UserProfile | null) ?? null;
}

async function loadConsumerProfile(userId: string): Promise<ConsumerProfile | null> {
  let res = await supabase
    .from("profiles")
    .select("id, display_name, theme")
    .eq("id", userId)
    .maybeSingle();
  if (res.error && res.error.code === "42703") {
    // §26 (profiles.theme) not run yet — retry without the column so the
    // whole profile load never breaks on a missing preference.
    res = await supabase.from("profiles").select("id, display_name").eq("id", userId).maybeSingle();
  }
  if (res.error) {
    console.warn("[authStore] consumer profile fetch failed:", res.error.message);
    return null;
  }
  return (res.data as ConsumerProfile | null) ?? null;
}

/** DB wins on load: a stored theme preference overrides this device's. */
function hydrateThemeFromProfile(profile: ConsumerProfile | null): void {
  const stored = profile?.theme;
  if (stored !== "dark" && stored !== "light") return;
  const themeStore = useThemeStore.getState();
  if (themeStore.theme !== stored) themeStore.setTheme(stored);
}

/** Best display name Google (or our sign-up metadata) gave us, else null. */
function displayNameFromMetadata(user: User): string | null {
  const meta = user.user_metadata ?? {};
  const raw = [meta.display_name, meta.full_name, meta.name].find(
    (v): v is string => typeof v === "string" && v.trim().length > 0,
  );
  return raw ? raw.trim().slice(0, 80) : null;
}

interface ResolvedProfiles {
  profile: UserProfile | null;
  consumerProfile: ConsumerProfile | null;
}

/**
 * A session resolves to staff XOR consumer. Staff wins (a users row exists);
 * otherwise the profiles row is fetched — and created on the very first
 * consumer session (client-side by design, schema §17: an auth.users trigger
 * would also fire for provisioner-created manager logins).
 */
async function resolveProfiles(user: User): Promise<ResolvedProfiles> {
  const [staff, consumer] = await Promise.all([
    loadStaffProfile(user.id),
    loadConsumerProfile(user.id),
  ]);
  if (staff) return { profile: staff, consumerProfile: null };
  if (consumer) {
    hydrateThemeFromProfile(consumer);
    return { profile: null, consumerProfile: consumer };
  }

  const { data, error } = await supabase
    .from("profiles")
    .upsert({ id: user.id, display_name: displayNameFromMetadata(user) }, { onConflict: "id" })
    .select("id, display_name")
    .single();
  if (error) {
    // Non-fatal: the account works without a profile row; the next session
    // (or a display-name edit) retries the upsert.
    console.warn("[authStore] consumer profile create failed:", error.message);
    return { profile: null, consumerProfile: null };
  }
  return { profile: null, consumerProfile: data as ConsumerProfile };
}

/**
 * Link this device's consented anonymous id to the signed-in consumer
 * (schema §18) so guest history survives account creation. Fire-and-forget,
 * silent-fail, once per (account, id) pair per page load. Safe to call any time — it no-ops
 * without consent, without a session, or for staff.
 */
// The id last sent for linking — a NEW id (re-allowed after "Forget this
// device", or a new account in the same tab) is linked again; the same one isn't.
let anonLinkedFor: string | null = null;
export function maybeLinkAnonId(): void {
  const state = useAuthStore.getState();
  if (!state.consumerProfile) return;
  const anon = getAnonId();
  if (!anon) return;
  const key = `${state.user?.id ?? ""}:${anon}`;
  if (anonLinkedFor === key) return;
  anonLinkedFor = key;
  void supabase.rpc("link_anon_id", { p_anon: anon }).then(({ error }) => {
    if (error) console.warn("[authStore] anon link failed:", error.message);
  });
}

let initialized = false;

export const useAuthStore = create<AuthState>((set, get) => ({
  status: "initializing",
  session: null,
  user: null,
  profile: null,
  consumerProfile: null,
  profileLoading: false,
  lastError: null,
  recoveryPending: false,

  initialize: async () => {
    if (initialized) return;
    initialized = true;

    const { data } = await supabase.auth.getSession();
    if (data.session) {
      // Sequential awaits here are safe — we're NOT inside an auth-state
      // callback, so no lock is held.
      set({
        status: "signed_in",
        session: data.session,
        user: data.session.user,
        profileLoading: true,
      });
      const resolved = await resolveProfiles(data.session.user);
      set({ ...resolved, profileLoading: false });
      if (resolved.consumerProfile) maybeLinkAnonId();
    } else {
      set({
        status: "signed_out",
        session: null,
        user: null,
        profile: null,
        consumerProfile: null,
        profileLoading: false,
      });
    }

    // Single subscription keeps every component in sync with auth changes.
    //
    // CRITICAL: never `await` a Supabase call inside this callback. GoTrue
    // invokes it while holding its internal auth lock; a nested query such as
    // resolveProfiles() re-enters `_acquireLock`, deadlocking the
    // `pendingInLock` queue. Once wedged, every later Supabase request (update
    // venue, list venues, sign-out) hangs until a full page reload resets the
    // in-memory client — exactly the "works only after refresh" symptom. So we
    // update the session synchronously and defer the profile fetch to a later
    // macrotask, outside the lock.
    supabase.auth.onAuthStateChange((event, session) => {
      // Recovery-link landing (PKCE code already exchanged by
      // detectSessionInUrl). Synchronous set only — see the lock rule above.
      if (event === "PASSWORD_RECOVERY") {
        set({ recoveryPending: true });
      }

      if (!session) {
        set({
          status: "signed_out",
          session: null,
          user: null,
          profile: null,
          consumerProfile: null,
          profileLoading: false,
          recoveryPending: false,
        });
        return;
      }

      const prev = get();
      const sameUser = prev.user?.id === session.user.id;
      set({ status: "signed_in", session, user: session.user });

      // Skip refetching the profile on plain token refreshes for the same
      // user — we already have it. Only fetch when the user changed or no
      // profile (staff or consumer) has resolved yet.
      if (sameUser && (prev.profile || prev.consumerProfile)) return;

      set({ profileLoading: true });
      setTimeout(() => {
        void (async () => {
          const resolved = await resolveProfiles(session.user);
          // Guard against a race where the user signed out / switched while
          // the fetch was in flight.
          if (get().user?.id === session.user.id) {
            set({ ...resolved, profileLoading: false });
            if (resolved.consumerProfile) maybeLinkAnonId();
          }
        })();
      }, 0);
    });
  },

  signIn: async (email, password, captchaToken) => {
    set({ lastError: null });
    const { error } = await supabase.auth.signInWithPassword({
      email,
      password,
      options: { captchaToken },
    });
    if (error) {
      set({ lastError: error.message });
      return { ok: false, error: error.message };
    }
    // onAuthStateChange will populate status / session / profile.
    return { ok: true };
  },

  signUp: async (email, password, displayName, captchaToken) => {
    const name = displayName.trim().slice(0, 80);
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        // The sign-up form can't submit until its 16+ box is ticked, so the
        // confirmation is recorded on the auth user alongside the name.
        data: { ...(name ? { display_name: name } : {}), age_16_confirmed: true },
        emailRedirectTo: `${window.location.origin}/app`,
        captchaToken,
      },
    });
    if (error) return { ok: false, error: error.message };
    // No session yet = the dashboard "Confirm email" toggle is on (or Supabase
    // returned its anti-enumeration fake user for an existing email) — either
    // way the correct UX is the check-your-inbox screen.
    if (!data.session) return { ok: true, needsEmailConfirm: true };
    return { ok: true };
  },

  signInWithGoogle: async () => {
    // Top-level navigation to Google; PKCE code comes back on /app where
    // detectSessionInUrl exchanges it. No CSP change involved.
    const { error } = await supabase.auth.signInWithOAuth({
      provider: "google",
      options: { redirectTo: `${window.location.origin}/app` },
    });
    return error ? { ok: false, error: error.message } : { ok: true };
  },

  signInWithApple: async () => {
    const { error } = await supabase.auth.signInWithOAuth({
      provider: "apple",
      options: { redirectTo: `${window.location.origin}/app` },
    });
    return error ? { ok: false, error: error.message } : { ok: true };
  },

  resetPassword: async (email, captchaToken) => {
    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: `${window.location.origin}/app`,
      captchaToken,
    });
    // Deliberately swallowed into a generic result — the sidebar shows "if an
    // account exists, an email is on its way" regardless, so this form can't
    // enumerate accounts. Real failures (rate limit, network) still surface.
    return error ? { ok: false, error: error.message } : { ok: true };
  },

  resendConfirmEmail: async (email, captchaToken) => {
    const { error } = await supabase.auth.resend({
      type: "signup",
      email,
      options: { emailRedirectTo: `${window.location.origin}/app`, captchaToken },
    });
    return error ? { ok: false, error: error.message } : { ok: true };
  },

  updatePassword: async (password) => {
    const { error } = await supabase.auth.updateUser({ password });
    if (error) return { ok: false, error: error.message };
    set({ recoveryPending: false });
    return { ok: true };
  },

  updateDisplayName: async (name) => {
    const { user } = get();
    if (!user) return { ok: false, error: "not signed in" };
    const trimmed = name.trim().slice(0, 80);
    if (!trimmed) return { ok: false, error: "empty name" };
    const { data, error } = await supabase
      .from("profiles")
      .upsert(
        { id: user.id, display_name: trimmed, updated_at: new Date().toISOString() },
        { onConflict: "id" },
      )
      .select("id, display_name")
      .single();
    if (error) return { ok: false, error: error.message };
    set({ consumerProfile: data as ConsumerProfile });
    return { ok: true };
  },

  updateTheme: async (theme) => {
    // Always applies on-device (guests + staff included) — the DB write below
    // is the cross-device extra for signed-in consumers.
    useThemeStore.getState().setTheme(theme);
    const { user, consumerProfile } = get();
    if (!user || !consumerProfile) return { ok: true };
    const { error } = await supabase
      .from("profiles")
      .upsert({ id: user.id, theme, updated_at: new Date().toISOString() }, { onConflict: "id" });
    if (error) {
      // Pre-§26 (missing column) or offline — the theme still applied locally.
      console.warn("[authStore] theme persist failed:", error.message);
      return { ok: true };
    }
    set({ consumerProfile: { ...consumerProfile, theme } });
    return { ok: true };
  },

  deleteAccount: async () => {
    const { error } = await supabase.rpc("delete_own_account");
    if (error) return { ok: false, error: error.message };
    // The auth user is already gone server-side (§31b erased its visits and
    // events; the cascade took the profile and anon links) — a local sign-out
    // clears the session, and the device keys (anon id, consent, tap history)
    // go too; onAuthStateChange resets the store.
    await clearDeviceAfterAccountDeletion();
    await supabase.auth.signOut({ scope: "local" });
    return { ok: true };
  },

  clearRecovery: () => set({ recoveryPending: false }),

  signOut: async () => {
    // Default scope is "global": every session of this account is revoked
    // ("sign out everywhere"), not just this tab's.
    await supabase.auth.signOut();
    // onAuthStateChange handles the state transition.
  },

  refreshProfile: async () => {
    const { user } = get();
    if (!user) return;
    const resolved = await resolveProfiles(user);
    set(resolved);
  },
}));
